/*
 * One sync run, and the rules about when to attempt another.
 *
 * ────────────────────────────────────────────────────────────────────────
 * navigator.onLine IS NOT A NETWORK TEST
 * ────────────────────────────────────────────────────────────────────────
 *
 * `online` means "this device has a link" -- a Wi-Fi association to a lab
 * access point whose uplink is down reports online, and so does a captive
 * portal. So `online` is a HINT that it is worth trying, never a promise, and
 * `offline` is the only one of the two worth trusting: a device that says it
 * is offline really cannot reach anything.
 *
 * The consequence is that a run must be allowed to fail for network reasons
 * without losing anything, and must not be treated as proof of connectivity
 * having returned. Nothing is dequeued until the server has answered.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHAT IS FORGOTTEN, AND WHEN
 * ────────────────────────────────────────────────────────────────────────
 *
 * Entries are removed only after a 2xx. A conflict is still a successful
 * sync: the server considered the record and declined it, which is an answer,
 * and re-sending it on the next run would produce the same answer forever.
 * So a conflicted entry leaves the queue too -- and is recorded in the
 * conflicts store, where a person can see what was dropped.
 *
 * That is the one place this design loses work, and it does so visibly rather
 * than silently. The alternative -- keeping it queued -- is an outbox that
 * never drains and a badge that never clears.
 */

export const SYNC_PATH = '/sync';
export const WATERMARK_KEY = 'pitriot.sync.lastSyncedAt';

export const IDLE = 'idle';
export const RUNNING = 'running';
export const OFFLINE = 'offline';
export const FAILED = 'failed';

/** Back off so a flapping connection does not become a request storm. */
export function backoffMs(attempt, { base = 2000, ceiling = 5 * 60 * 1000 } = {}) {
  return Math.min(ceiling, base * (2 ** Math.max(0, attempt - 1)));
}

export function readWatermark(storage) {
  try { return storage.getItem(WATERMARK_KEY) || null; } catch { return null; }
}

export function writeWatermark(storage, value) {
  try { storage.setItem(WATERMARK_KEY, value); } catch { /* private mode: next run pulls more */ }
}

/**
 * Should a run be attempted right now?
 *
 * Refusing early keeps the log readable and stops a phone in a cellar from
 * burning battery on requests that cannot leave it.
 */
export function shouldSync({ online, running, pendingCount, nextAttemptAt, now = Date.now() }) {
  if (running) return { go: false, why: 'already_running' };
  if (online === false) return { go: false, why: 'offline' };
  if (nextAttemptAt && now < nextAttemptAt) return { go: false, why: 'backing_off' };
  if (!pendingCount) return { go: false, why: 'nothing_queued' };
  return { go: true, why: 'ready' };
}

/**
 * Run one sync.
 *
 * Takes its collaborators rather than reaching for them, so the whole thing
 * is testable without a network, a database or a browser.
 */
export async function runSync({ api, queue, collapse, storage, onServerChanges }) {
  const entries = await queue.pending();
  const { changes, entryIds } = collapse(entries);

  const body = {
    last_synced_at: readWatermark(storage),
    changes,
  };

  let data;
  try {
    ({ data } = await api.post(SYNC_PATH, body));
  } catch (error) {
    // Nothing is forgotten. The queue is exactly as it was, and the next
    // attempt sends the same thing.
    return {
      ok: false,
      state: error?.response ? FAILED : OFFLINE,
      error: error?.response?.data?.error || error?.message || String(error),
      sent: entryIds.length,
    };
  }

  // The server considered every record it was sent, including the ones it
  // declined, so all of them leave the queue.
  await queue.forget(entryIds);
  if (data.conflicts?.length) await queue.recordConflicts(data.conflicts);
  if (data.server_time) writeWatermark(storage, data.server_time);
  if (onServerChanges && data.changes) await onServerChanges(data.changes);

  return {
    ok: true,
    state: IDLE,
    sent: entryIds.length,
    applied: data.applied || {},
    conflicts: data.conflicts || [],
    received: Object.values(data.changes || {}).reduce((n, rows) => n + rows.length, 0),
    // The server caps what it returns; more waits for the next run rather
    // than being lost.
    more: Boolean(data.more),
  };
}

/**
 * Describe a conflict in words a person can act on.
 *
 * The server's `reason` is a token for a program. This is for whoever is
 * holding the phone, and it says which way the loss went.
 */
export function explainConflict(conflict) {
  switch (conflict?.reason) {
    case 'reject_older':
      return 'someone changed this after you did, so your edit was not applied';
    case 'reject_same':
      return 'this was already saved; your copy matched what the server had';
    case 'reject_no_timestamp':
      return 'this change had no timestamp, so it could not be ordered against the server copy';
    // Delete wins (server routes/sync.py). A deleted record is not edited or
    // brought back by a queued change -- a collection manager restores it.
    case 'deleted':
      return 'this record was deleted on the server, so your edit was not applied. A collection manager can restore it';
    case 'parent_deleted':
      return 'the record this belongs to was deleted on the server, so your change was not applied';
    case 'refused':
      // The server says WHY in `detail` -- "you can only change your own
      // collections", "this collection has been submitted". Those sentences
      // are written for a person to read, so they are passed through rather
      // than replaced with a generic line.
      return conflict.detail
        ? `this change was not allowed: ${conflict.detail}`
        : 'this change was not allowed';
    default:
      return 'this change was not applied';
  }
}

/**
 * Conflicts worth interrupting somebody for. A tie lost nothing.
 *
 * Stated as "everything except a tie" rather than as a list of the reasons
 * that lose work. This was a list, and it was the wrong shape: when the
 * server gained `refused` -- the one reason where somebody is told they were
 * not ALLOWED to do something -- the list silently did not match it, so the
 * banner stayed shut and the edit disappeared without a word. An unknown
 * reason means the server did not apply something and could not be sure
 * enough to say it cost nothing, so it gets shown.
 */
export function losesWork(conflict) {
  return !!conflict && conflict.reason !== 'reject_same';
}
